// DIB (Dubai Islamic Bank) — Shams Infinite Credit Card.
// Columns: postDate@~40-53 | trxnDate@~118-133 | refNo@~198-207 | description@~259 | amount@~534-548
// Summary grid (page 1): statementDate, creditLimit, closingBalance,
//   minimumPayment, dueDate all in one row. Available limit in a separate row.
// Foreign amounts embedded in description as "(CCY nnn.nn )".
// Credits: "nnn.nn CR" with a space before CR.
//
// COLUMN BANDS ARE DELIBERATELY WIDE. DIB emits the same visual layout with the
// text layer shifted horizontally by ~5-19px between PDF templates (e.g. one
// batch puts the posting date at x=53, another at x=39). Columns sit ~80-100px
// apart, so each band is padded generously toward its neighbours without any
// risk of collision — a narrow band silently drops every row (0 txns parsed).
import { toNum, dmyToISO, amountWithSign } from '../util.js';

// Wala'a rewards redeem at 250 points = AED 1 (per statement: 60,000 pts → AED 240).
const DIB_AED_PER_POINT = 0.004;

export function parseDIB(pages) {
  const p1 = pages[0] || [];
  const byY1 = {};
  for (const it of p1) { const k = Math.round(it.y / 3) * 3; (byY1[k] = byY1[k] || []).push(it); }

  const summary = {
    statementDate: null, dueDate: null,
    minimumPayment: null, creditLimit: null, availableLimit: null, closingBalance: null,
    previousBalance: null, cashbackBalance: null, cashbackEarned: null, cashbackRedeemed: null,
  };

  // Main summary row: date@59, creditLimit@153, closingBalance@249, minPayment@351, dueDate@434.
  // The values aren't perfectly y-aligned — the Total Amount Due can sit a few
  // px above the rest of the row — so once the date+limit anchor the row, the
  // other cells are searched within ±5px of it, not just the exact y-band.
  for (const y of Object.keys(byY1).map(Number).sort((a, b) => b - a)) {
    const cells = byY1[y].sort((a, b) => a.x - b.x);
    const dateCell  = cells.find(c => c.x >= 30 && c.x <= 90 && /^\d{2}\/\d{2}\/\d{4}$/.test(c.s));
    const limitCell = cells.find(c => c.x >= 110 && c.x <= 190 && /^[\d,]+\.\d{2}$/.test(c.s));
    if (dateCell && limitCell) {
      const near = p1.filter(c => Math.abs(c.y - dateCell.y) <= 5);
      summary.statementDate = dmyToISO(dateCell.s);
      summary.creditLimit   = toNum(limitCell.s);
      const closeCell = near.find(c => c.x >= 200 && c.x <= 285 && /^[\d,]+\.\d{2}$/.test(c.s));
      if (closeCell) summary.closingBalance = toNum(closeCell.s);
      const minCell   = near.find(c => c.x >= 295 && c.x <= 385 && /^[\d,]+\.\d{2}$/.test(c.s));
      if (minCell)   summary.minimumPayment = toNum(minCell.s);
      const dueCell   = near.find(c => c.x >= 395 && c.x <= 468 && /^\d{2}\/\d{2}\/\d{4}$/.test(c.s));
      if (dueCell)   summary.dueDate = dmyToISO(dueCell.s);
      break;
    }
  }

  // Second summary row: Previous Balance | Monthly Usage | Payments | Available
  // Credit Limit | Available Cash Limit. previousBalance is the 1st value (x≈71),
  // availableLimit the 4th (x≈396).
  for (const y of Object.keys(byY1).map(Number).sort((a, b) => b - a)) {
    const cells = byY1[y].sort((a, b) => a.x - b.x);
    const nums = cells.filter(c => /^[\d,]+\.\d{2}$/.test(c.s));
    if (nums.length >= 4 && nums[0].x >= 45 && nums[0].x <= 95) {
      summary.previousBalance = toNum(nums[0].s);
      const avail = nums.find(c => c.x >= 368 && c.x <= 425);
      if (avail) { summary.availableLimit = toNum(avail.s); break; }
    }
  }

  // Wala'a Rewards Statement (page 5): four column headers
  //   Previous Balance | Current Month's Earning | Current Month's Redemption | Closing Balance
  // over a single row of four integer point values. Anchor on the page carrying
  // both "Earning" and "Redemption", grab the row of exactly four plain integers
  // (point values have no decimals; AED amounts do), then convert points → AED.
  const rewardsPage = pages.find(p =>
    p.some(i => /Earning/i.test(i.s)) && p.some(i => /Redemption/i.test(i.s)));
  if (rewardsPage) {
    const byYr = {};
    for (const it of rewardsPage) { const k = Math.round(it.y / 3) * 3; (byYr[k] = byYr[k] || []).push(it); }
    for (const k of Object.keys(byYr).map(Number).sort((a, b) => b - a)) {
      const ints = byYr[k].sort((a, b) => a.x - b.x).filter(c => /^\d{3,7}$/.test(c.s));
      if (ints.length === 4) {
        const pts = ints.map(c => toNum(c.s)); // [previous, earned, redeemed, closing]
        const toAed = (p) => p == null ? null : Math.round(p * DIB_AED_PER_POINT * 100) / 100;
        summary.cashbackEarned   = toAed(pts[1]);
        summary.cashbackRedeemed = toAed(pts[2]);
        summary.cashbackBalance  = toAed(pts[3]);
        break;
      }
    }
  }

  const txns = [];
  for (const page of pages) {
    const byY = {};
    for (const it of page) { const k = Math.round(it.y / 3) * 3; (byY[k] = byY[k] || []).push(it); }
    const sortedYs = Object.keys(byY).map(Number).sort((a, b) => b - a);

    for (let i = 0; i < sortedYs.length; i++) {
      const y = sortedYs[i];
      const cells = byY[y].sort((a, b) => a.x - b.x);

      const postCell = cells.find(c => c.x >= 30 && c.x <= 90 && /^\d{2}\/\d{2}\/\d{4}$/.test(c.s));
      if (!postCell) continue;
      const trxnCell = cells.find(c => c.x >= 95 && c.x <= 180 && /^\d{2}\/\d{2}\/\d{4}$/.test(c.s));
      if (!trxnCell) continue;

      // Amount: rightmost "nnn.nn"/"nnn.nn CR" cell in the amount column. The
      // column is right-aligned, so a WIDE amount (e.g. 16,884.88) starts
      // further left — its left edge can sit at x≈520. Threshold ≥490 catches
      // those while staying well clear of the description column (x≈263).
      const amountCell = [...cells].reverse().find(c =>
        c.x >= 460 && /^[\d,]+\.\d{2}(\s*CR)?$/.test(c.s));
      if (!amountCell) continue;

      // Description: cells at x≥255, excluding the amount cell
      let descCells = cells.filter(c => c.x >= 255 && c !== amountCell);
      let desc = descCells.map(c => c.s).join(' ').replace(/\s{2,}/g, ' ').trim();

      // Some rows (e.g. EPP Installment) have no inline description — look at the row above
      if (!desc && i > 0) {
        const prevY = sortedYs[i - 1];
        const prevCells = (byY[prevY] || []).filter(c => c.x >= 255);
        desc = prevCells.map(c => c.s).join(' ').replace(/\s{2,}/g, ' ').trim();
      }
      if (!desc) continue;

      // Skip the PREVIOUS BALANCE header row (no amount cell will match above, but guard anyway)
      if (/^PREVIOUS\s+BALANCE$/i.test(desc)) continue;

      const { amount, credit } = amountWithSign(amountCell.s);
      if (amount == null) continue;

      // Foreign original — two layouts: "MERCHANT(CCY nnn.nn ), CC" or
      // "… AMNT IN FX <CCY> <amt>: AMNT IN BILLED AED <aed> …". The Amount column
      // is ALWAYS AED (billed); the foreign figure is kept as originalAmount so
      // the tx stores AED + the original side-by-side (currency stays AED).
      const paren  = desc.match(/\(([A-Z]{3})\s+([\d,]+(?:\.\d+)?)\s*\)/);
      const fxLine = desc.match(/AMNT\s+IN\s+FX\s+([A-Z]{3})\s+([\d,]+(?:\.\d+)?)/i);
      const fx = paren || fxLine;
      const originalCurrency = fx ? fx[1] : null;
      const originalAmount   = fx ? toNum(fx[2]) : null;

      const refCell = cells.find(c => c.x >= 180 && c.x <= 240 && /^\d{6,10}$/.test(c.s));

      const tag = /INTL\s+DCC\s+FEE/i.test(desc) ? 'fee'
        : /PAYMENT.*RECEIVED|RECEIVED.*THANK\s+YOU/i.test(desc) ? 'payment'
        : /REWARDS.*REDEMPTION|CASHBACK.*REDEMPTION/i.test(desc) ? 'cashback'
        : /PROFIT.*REBATE|DISCRETIONARY.*REBATE|PROFIT\s+EARNED|DISCRETIONARY\s+REWARD/i.test(desc) ? 'islamic_net'
        : /MONTHLY.*PAYMENT.*FOR.*SALAM/i.test(desc) ? 'islamic_net'
        : /EPP\s+INSTALLMENT/i.test(desc) ? 'installment'
        : null;

      txns.push({
        date: dmyToISO(trxnCell.s),
        postingDate: dmyToISO(postCell.s),
        refNo: refCell ? refCell.s : null,
        description: desc,
        amount,
        credit,
        currency: 'AED',
        originalCurrency,
        originalAmount,
        tag,
      });
    }
  }

  return { bank: 'DIB', summary, transactions: txns };
}
